import { useState, useEffect } from "react";
import api from "api";

/**
 * Busca todos os arquivos de um determinado productType em todas as datas do projeto.
 *
 * @param {string} token        – JWT do usuario
 * @param {string} projectName  – nome do projeto (ex: "obra-centro")
 * @param {string} productType  – tipo de produto nas dates: "geoview" | "tour_360" | "gemeo_digital"
 * @returns {{ allDates: Array<{dateLabel: string, files: Array}>, loading: boolean }}
 */
export default function useAllProjectFiles(token, projectName, productType) {
  const [allDates, setAllDates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token || !projectName || !productType) return;

    let cancelled = false;

    const fetchAll = async () => {
      setLoading(true);
      try {
        const res = await api.get("/projects/user", {
          headers: { Authorization: `Bearer ${token}` }
        });
        const projects = Array.isArray(res.data)
          ? res.data
          : res.data?.projects || [];
        const proj = projects.find(p => p.name === projectName);
        if (!proj?.dates) {
          setAllDates([]);
          return;
        }

        const productAliases =
          productType === "geoview" ? ["geoview", "ortomosaico", "geotiff"] : [productType];

        const matchingDates = proj.dates.filter(d =>
          d.products?.some(p => productAliases.includes(p))
        );

        const results = await Promise.allSettled(
          matchingDates.map(async d => {
            for (const alias of productAliases) {
              const r = await api.post(
                "/projects/list_files",
                { folder_path: `projetos/${projectName}/${d.date}/${alias}` },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              const files = r.data?.files || [];
              if (files.length > 0) return { dateLabel: d.date, files };
            }
            return { dateLabel: d.date, files: [] };
          })
        );

        if (cancelled) return;

        const dated = results
          .filter(r => r.status === "fulfilled" && r.value.files.length > 0)
          .map(r => r.value);

        setAllDates(dated);
      } catch (err) {
        console.error(`[useAllProjectFiles] Erro ao buscar ${productType}`, err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAll();
    return () => { cancelled = true; };
  }, [token, projectName, productType]);

  return { allDates, loading };
}
